import { Flex, Skeleton, SkeletonCircle, SkeletonText, VStack } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { type ReactElement, useState } from 'react'

import type { BreakoutSessionBannerData } from '@/common/services/seriesPage/types'

import { HasuraContextWrapper } from '@/client/context/hasura'
import { useServerNow } from '@/client/hooks'
import useInterval from '@/client/hooks/useInterval'
import { findNextSession } from '@/client/services/seriesPage/helpers'

import Card from '@/components/Worlds/UI/Card'

import BreakoutSessionJoinBanner from './internal/BreakoutSessionJoinBanner'
import BreakoutSessionManageBanner from './internal/BreakoutSessionManageBanner'

const TUTOR_EARLY_ACCESS_MINUTES = 15
const LEARNER_EARLY_ACCESS_MINUTES = 0
const REFRESH_INTERVAL_SECONDS = 1

function BreakoutSessionBannerSkeleton({ isUserTutor }: { isUserTutor: boolean }): ReactElement {
  return isUserTutor ? (
    <Card width="100%" mb={4}>
      <VStack align="stretch" spacing={6}>
        <Skeleton height="32px" width="180px" />
        <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
          <SkeletonText mt="2" noOfLines={3} spacing="4" skeletonHeight="20px" width="100%" />
          <SkeletonText mt="2" noOfLines={3} spacing="4" skeletonHeight="20px" width="100%" />
        </Flex>
      </VStack>
    </Card>
  ) : (
    <Card width="100%" mb={4}>
      <VStack spacing={6} align="center" py={4}>
        <SkeletonCircle size="16" />
        <SkeletonText mt="2" noOfLines={2} spacing="4" skeletonHeight="20px" width="100%" />
        <Skeleton height="40px" width="120px" borderRadius="md" />
      </VStack>
    </Card>
  )
}

export default function BreakoutSessionBanner({
  series,
}: {
  series: BreakoutSessionBannerData
}): ReactElement | null {
  const now = useServerNow()
  const [showManageBanner, setShowManageBanner] = useState(false)
  const [showJoinBanner, setShowJoinBanner] = useState(false)

  const isUserTutor = series.viewer.isHost
  const isUserRegistered = series.viewer.isRegisteredLearner
  const isParticipant = isUserRegistered || isUserTutor

  const nextSession = findNextSession(series.sessions, now)

  const sessionId = nextSession?.id ?? 0
  const sessionStartDate = nextSession?.startDate ?? null

  const isBreakoutEnabledSession = nextSession?.isBreakoutEnabled ?? false
  useInterval(
    () => {
      if (sessionStartDate == null) {
        return
      }

      const currentTime = DateTime.now()
      const startTime = DateTime.fromISO(sessionStartDate)

      setShowManageBanner(
        isUserTutor && startTime.minus({ minutes: TUTOR_EARLY_ACCESS_MINUTES }) < currentTime
      )

      setShowJoinBanner(startTime.minus({ minutes: LEARNER_EARLY_ACCESS_MINUTES }) < currentTime)
    },
    REFRESH_INTERVAL_SECONDS * 1000,
    [showManageBanner, showJoinBanner, isUserTutor, sessionStartDate]
  )

  if (!isParticipant || sessionStartDate == null || !isBreakoutEnabledSession) {
    return null
  }

  return (
    <HasuraContextWrapper
      LoadingComponent={() => <BreakoutSessionBannerSkeleton isUserTutor={isUserTutor} />}
    >
      {showManageBanner ? <BreakoutSessionManageBanner sessionId={sessionId} /> : null}
      {showJoinBanner ? <BreakoutSessionJoinBanner sessionId={sessionId} /> : null}
    </HasuraContextWrapper>
  )
}
